import vkBridge from '@vkontakte/vk-bridge';

export interface VKLaunchParams {
  vk_user_id?: string;
  vk_language?: string;
  vk_ref?: string;
  sign?: string;
  vk_time?: string;
  [key: string]: string | undefined;
}

/** Collect launch params from search + hash (VK passes them in either place). */
export function getVKLaunchParams(): VKLaunchParams {
  const sources: string[] = [];
  if (window.location.search.length > 1) sources.push(window.location.search.slice(1));
  const hash = window.location.hash;
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) sources.push(hash.slice(qIndex + 1));
  else if (hash.length > 1 && hash.includes('=')) sources.push(hash.slice(1));

  const out: VKLaunchParams = {};
  for (const src of sources) {
    for (const pair of src.split('&')) {
      if (!pair) continue;
      const eq = pair.indexOf('=');
      if (eq < 0) continue;
      const key = decodeURIComponent(pair.slice(0, eq));
      const value = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' '));
      if (key && !(key in out)) out[key] = value;
    }
  }
  return out;
}

/** Raw query string for backend sign verification (no leading '?'). */
export function getVKRawQuery(): string {
  const search = window.location.search.length > 1 ? window.location.search.slice(1) : '';
  const hash = window.location.hash;
  const qIndex = hash.indexOf('?');
  const hashQuery = qIndex >= 0 ? hash.slice(qIndex + 1) : '';
  return search || hashQuery;
}

export function isVKEnvironment(): boolean {
  const p = getVKLaunchParams();
  return !!(p.vk_user_id && p.sign);
}

export interface VKUserProfile {
  vk_id: number;
  first_name: string;
  last_name?: string;
  avatar_url?: string;
  sign?: string;
  raw_query: string;
}

/**
 * Build VK user profile from launch params.
 * Prefer launch params (signed). Fall back to VKWebAppGetUserInfo in VK.
 */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('VK bridge timeout')), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

export async function getVKUserProfile(): Promise<VKUserProfile | null> {
  const params = getVKLaunchParams();
  const raw = getVKRawQuery();

  if (params.vk_user_id) {
    let first_name = 'User';
    let last_name = '';
    let avatar_url: string | undefined;

    try {
      const info = await withTimeout(vkBridge.send('VKWebAppGetUserInfo'), 3000);
      if (info && info.id === Number(params.vk_user_id)) {
        first_name = info.first_name || first_name;
        last_name = info.last_name || '';
        avatar_url = info.photo_200 || info.photo_100 || undefined;
      }
    } catch {
      // not in VK webview or timeout — keep defaults
    }

    return {
      vk_id: Number(params.vk_user_id),
      first_name,
      last_name,
      avatar_url,
      sign: params.sign,
      raw_query: raw,
    };
  }

  try {
    const info = await withTimeout(vkBridge.send('VKWebAppGetUserInfo'), 3000);
    return {
      vk_id: info.id,
      first_name: info.first_name,
      last_name: info.last_name,
      avatar_url: info.photo_200 || info.photo_100,
      raw_query: raw,
    };
  } catch {
    return null;
  }
}

/** Legacy helper kept for compatibility. */
export async function getVKUserData() {
  const profile = await getVKUserProfile();
  if (!profile) return null;
  return {
    vk_id: profile.vk_id,
    first_name: profile.first_name,
    last_name: profile.last_name,
    avatar_url: profile.avatar_url,
  };
}

export function getVKLaunchParamsString(): string {
  return getVKRawQuery();
}
